import { useAccount, useCurrentUser, Template } from "@zapier/client";
import { BetaColumnHeader, SortDescriptor } from "@zapier/design-system";
import { useMemo, useState } from "react";
import { useTranslations } from "@zapier/i18n";
import { Table, useRouterParams } from "@zapier/shared";

import { EmptyTemplatesState } from "./EmptyTemplatesState";
import TemplateRow from "./TemplateRow";
import { TemplateChangeOwnerModal } from "./TemplateChangeOwnerModal/TemplateChangeOwnerModal";
import { TemplateDeleteModal } from "./TemplateDeleteModal/TemplateDeleteModal";
import { TemplateDuplicateModal } from "./TemplateDuplicateModal/TemplateDuplicateModal";
import { TemplateShareModal } from "./TemplateShareModal";
import styles from "./TemplatesTable.module.css";
import { useShowEmptyTemplatesTable } from "./useShowEmptyTemplatesTable";

interface TemplatesTableProps {
  templates: Template[];
  isLoading: boolean;
  sortDescriptor: SortDescriptor;
  onSortChange: (sortDescriptor: SortDescriptor) => void;
}

const TemplatesTable = ({
  templates,
  isLoading,
  onSortChange,
  sortDescriptor,
}: TemplatesTableProps) => {
  const { search, shared, app, owner } = useRouterParams();
  const hasSharedFilter = shared && shared !== "all";
  const isFiltered = !!(search || app || owner || hasSharedFilter);
  const t = useTranslations("templates-table");
  const [shareModalTemplate, setShareModalTemplate] = useState<
    Template | undefined
  >();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [deleteModalTemplate, setDeleteModalTemplate] = useState<
    Template | undefined
  >();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [changeOwnerModalTemplate, setChangeOwnerModalTemplate] = useState<
    Template | undefined
  >();
  const [isChangeOwnerModalOpen, setIsChangeOwnerModalOpen] = useState(false);
  const [duplicateModalTemplate, setDuplicateModalTemplate] = useState<
    Template | undefined
  >();
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const { account } = useAccount();
  const { user } = useCurrentUser();
  const showOrganizationTab = account?.planInfo?.allowTeams ?? false;
  const isWorkspace = account?.parentAccountRelationship === "workspace";
  // Gated on a loaded account so the locked tab never flashes for team plans.
  const isOrganizationTabUpgradeLocked =
    Boolean(account) && !showOrganizationTab && !isWorkspace;
  const showWorkspaceTab = isWorkspace;
  const organizationName = isWorkspace
    ? (account?.parentAccount?.name ?? account?.name ?? undefined)
    : (account?.name ?? undefined);
  const workspaceName = isWorkspace ? (account?.name ?? undefined) : undefined;

  const { showEmptyState, emptyType } = useShowEmptyTemplatesTable({
    templates,
    isLoading,
    isFiltered,
    shared,
  });

  const columns = useMemo(
    () =>
      [
        {
          sortKey: "apps",
          label: t("columns.apps"),
          allowsSorting: false,
          className: styles.appsColumn,
          defaultWidth: 85,
          minWidth: 85,
          maxWidth: 85,
          allowsResizing: false,
        },
      ].filter(Boolean) as BetaColumnHeader[],
    [t],
  );

  if (showEmptyState && emptyType) {
    return <EmptyTemplatesState tableType={emptyType} />;
  }

  return (
    <>
      <Table
        allowSortByTitle={true}
        ariaLabel="Templates"
        columns={columns}
        isLoading={isLoading}
        onSortChange={onSortChange}
        showCheckboxes={false}
        showOwner={true}
        showFavoriteButton={false}
        showOptions={true}
        sortDescriptor={sortDescriptor}
        titleSortKey="name"
        className={styles.templatesTable}
        ownerColumnClassName={styles.ownerColumnHeaderCentered}
      >
        {templates?.map((template) => (
          <TemplateRow
            key={template.id}
            onDeleteTemplate={() => {
              setDeleteModalTemplate(template);
              setIsDeleteModalOpen(true);
            }}
            onShareTemplate={() => {
              setShareModalTemplate(template);
              setIsShareModalOpen(true);
            }}
            onChangeOwnerTemplate={() => {
              setChangeOwnerModalTemplate(template);
              setIsChangeOwnerModalOpen(true);
            }}
            onDuplicateTemplate={() => {
              setDuplicateModalTemplate(template);
              setIsDuplicateModalOpen(true);
            }}
            template={template}
            searchQuery={search}
          />
        ))}
      </Table>
      <TemplateShareModal
        isOpen={isShareModalOpen}
        onOpenChange={(open) => {
          setIsShareModalOpen(open);
          if (!open) setShareModalTemplate(undefined);
        }}
        organizationName={organizationName}
        isOrganizationTabUpgradeLocked={isOrganizationTabUpgradeLocked}
        showOrganizationTab={showOrganizationTab}
        showWorkspaceTab={showWorkspaceTab}
        template={shareModalTemplate}
        workspaceName={workspaceName}
      />
      <TemplateChangeOwnerModal
        isOpen={isChangeOwnerModalOpen}
        onCloseModal={() => {
          setIsChangeOwnerModalOpen(false);
          // Clear template after isOpen flips so modal content stays visible during close animation
          setTimeout(() => setChangeOwnerModalTemplate(undefined), 0);
        }}
        template={changeOwnerModalTemplate}
      />
      <TemplateDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          // Clear template after isOpen flips so modal content stays visible during close animation
          setTimeout(() => setDeleteModalTemplate(undefined), 0);
        }}
        template={deleteModalTemplate}
      />
      <TemplateDuplicateModal
        isOpen={isDuplicateModalOpen}
        onClose={() => {
          setIsDuplicateModalOpen(false);
          // Clear template after isOpen flips so modal content stays visible during close animation
          setTimeout(() => setDuplicateModalTemplate(undefined), 0);
        }}
        template={duplicateModalTemplate}
        customUserId={user?.id}
      />
    </>
  );
};

export default TemplatesTable;
